import type { Intersection } from 'three';
import type { RootState } from '@react-three/fiber/dist/declarations/src/core/store';
import RaycasterClass from '@modules/three/common/raycast';
import type { ITool } from '@modules/three/tools/ITool';
import type { ThreeRootState } from '@modules/three/common/BaseThreeScene';
import type JawSegmentation from '@segmentation/three/JawSegmentation';
import { gumEditStore } from '@segmentation/store/gumEditStore';

export interface GumTrimHost {
  getJaw(): JawSegmentation | null;
  /** Toggle orbit controls (off while dragging the trim margin). */
  setControlsEnabled(enabled: boolean): void;
}

/**
 * Pointer tool for the review-step gum trim. Press-drag on the gum sets the
 * collar margin to the dragged point's distance from the nearest tooth — the
 * cut boundary follows the cursor. The removal zone is tinted live.
 */
export class GumTrimTool implements ITool {
  readonly name = 'gum-trim';

  private dragging = false;

  constructor(private readonly host: GumTrimHost) {}

  onDeactivate(): void {
    this.dragging = false;
    this.host.setControlsEnabled(true);
  }

  onPointerDown(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    const hit = this.raycastJaw(ev, three);
    if (!hit) return;
    this.dragging = true;
    this.host.setControlsEnabled(false);
    this.updateMarginFromHit(hit);
  }

  onPointerMove(ev: PointerEvent, _hit: Intersection | null, three: RootState | ThreeRootState): void {
    if (!this.dragging) return;
    const hit = this.raycastJaw(ev, three);
    if (hit) this.updateMarginFromHit(hit);
  }

  onPointerUp(): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.host.setControlsEnabled(true);
  }

  private updateMarginFromHit(hit: Intersection): void {
    const jaw = this.host.getJaw();
    if (!jaw) return;
    const distance = jaw.gumTrimDistanceAtWorldPoint(hit.point);
    if (distance === null) return;
    // Signed distance: dragging inside the trim curve clamps to 0 (curve hugs the teeth).
    const margin = Math.max(0, Math.round(distance * 10) / 10);
    gumEditStore.getState().setTrimMargin(margin);
    jaw.previewGumTrimByTeeth(margin);
  }

  private raycastJaw(ev: PointerEvent, three: RootState | ThreeRootState): Intersection | null {
    const jaw = this.host.getJaw();
    if (!jaw) return null;
    return RaycasterClass.getFirstObjectIntersection(ev, jaw, three.camera, false);
  }
}
